import { Component, Vue, Provide, Prop, Inject } from 'vue-property-decorator';
import { ApiErrorResponse, createDtoEvent, SelectListItemWithSubtext } from '@/common/types/AllTypesExports';
import { IEventRegistrationGlobals } from './EventRegistrationGlobals';
import { eventRepo } from '../../common/repositories/EventRepo';
import NoviStepper from '../../components/stepper/NoviStepper.vue'
import Step, { createStep } from '../../components/stepper/Step';
import ErrorAlert from '../../components/errors/ErrorAlert.vue';
import ValidationErrors from '../../components/errors/ValidationErrors.vue';
import { DtoEvent } from '../../common/types/event/DtoEvent';
import ValidationService from '../../common/services/ValidationService';
import { AxiosResponse } from 'axios';
import EventRegistrationTickets from '../../components/event/EventRegistrationTickets.vue';
import AttendeeSelector from '../../components/event/AttendeeSelector.vue';
import EventAttendeeForm from '../../components/event/EventAttendeeForm.vue';                                                                                                                                                                                                                           
import { Multiselect } from 'vue-multiselect';
import { createAttendeeFromSelectListItemWithSubText, createAttendee, Attendee, DtoAttendee } from '../../common/types/event/DtoAttendee';
import dayjs from 'dayjs';
import { createEventRegistration, EventRegistration } from '../../common/types/event/DtoEventRegistration';
import { TicketsAndAddons, createTicketsAndAddons } from '../../common/types/event/DtoTicketsAndAddons';
import EventCheckout from '../../components/event/EventCheckout.vue';
import { customerRepo } from '../../common/repositories/CustomerRepo';
import { DtoCurrentUser, createDtoCurrentUser } from '@/common/types/user/DtoCurrentUser';
import NoviTinyMce from '../../components/inputs/tinymce/NoviTinyMce.vue';
import { Addon } from '../../common/types/event/DtoAddon';
import InfoAlert from '../../components/errors/InfoAlert.vue';
import cloneDeep from 'lodash/cloneDeep';
import { Ticket } from '../../common/types/event/DtoTicket';
import debounce from 'lodash/debounce';
import EventBus from '@/common/services/EventBus';
import EventGuestForm from '../../components/event/EventGuestForm.vue';

declare let noviNs: IEventRegistrationGlobals;

@Component({
    name: 'EventRegistration',
    components: {
        NoviStepper,
        ErrorAlert,
        ValidationErrors,
        EventRegistrationTickets,
        AttendeeSelector,
        Multiselect,
        EventAttendeeForm,
        EventCheckout,
        NoviTinyMce,
        InfoAlert,
        EventGuestForm
    }
})
export default class EventRegistrationPage extends Vue {

    @Provide('event')
    event: DtoEvent = createDtoEvent();

    eligibleAttendees: Attendee[] = [];

    currentAttendeeIndex: number = 0;

    @Provide('registration')
    registrationModel: EventRegistration = createEventRegistration();

    @Provide('ticketsAndAddons')
    ticketsAndAddons: TicketsAndAddons = createTicketsAndAddons();

    @Provide('user')
    currentUser: DtoCurrentUser = createDtoCurrentUser();

    hideGuestLoginAlert: boolean = false;

    guestEmail: string = '';

    errorMessage: string = '';
    globalErrorMessage: string = '';

    steps: Array<Step> = [];
    currentStep: Step = createStep();

    hasErrorOnContinue: boolean = false;
    isSaving: boolean = false;
    isLoadingEvent: boolean = true;
    isLoadingEligibleAttendees: boolean = true;
    isLoadingTicketsAndAddons: boolean = true;
    isRegisteringAsGuest = false;

    private emailToCheck: string = '';

    @Provide('validator')
    $validator: any = this.$validator;

    @Inject('registration')
    registration!: EventRegistration;

    // init calls
    mounted() {
        this.registrationModel.currencyIcon = noviNs.alternatePaymentCurrencyIcon;

        Promise.all([
            this.loadEvent(),
            this.loadCurrentUser()
        ]).then(() => {
            if (this.currentUser.isLoggedIn && !this.event.alreadyAttending && this.abandonmentRemindersEnabled && !this.isImpersonating) {
                this.updateCustomerRegistrationActivity();
            }
        });

        if (this.event.attendeeBased) {
            this.loadEligibleAttendees();
            this.loadTicketsAndAddons();
        }
        else {
            this.loadTicketsAndAddons();
            this.loadEligibleAttendees();
        }

        this.resetFocus();
    }

    // Computed Variables
    get shippingAddressInstructions(): string { return noviNs.shippingAddressInstructions };
    get professionalEmailInstructions(): string { return noviNs.professionalEmailInstructions };
    get parentMemberInstructions(): string { return noviNs.parentMemberInstructions };
    get parentMemberLabel(): string { return noviNs.parentLabel };
    get parentMemberRequired(): boolean { return noviNs.parentRequired };

    get nextStepText() {
        return 'Continue';
    }

    get previousStepText() {
        return 'Back';
    }

    get showNext() {
        if (this.currentStep == this.checkoutStep) {
            return false;
        }

        return true;
    }

    get showBack() {
        return this.steps.indexOf(this.currentStep) != 0;
    }

    get disableNext() {
        return (this.errors && this.errors.count() > 0) || this.isSaving;
    }

    get isLoading() {
        return this.isLoadingEvent || this.isLoadingEligibleAttendees || this.isLoadingTicketsAndAddons;
    }

    get currentAttendee() {
        if (this.registrationModel && this.registrationModel.attendees && this.registrationModel.attendees.length > 0) {
            return this.registrationModel.attendees[this.currentAttendeeIndex];
        }
        return createAttendee();
    }

    get previouslyPurchasedTicketIDs() {
        return this.eligibleAttendees?.flatMap(a => a.alreadyPurchasedTickets || []).filter(Boolean);
    }

    get currentTicketKey() {
        if (this.event.attendeeBased) {
            return this.currentAttendeeIndex + '-' + this.currentAttendee.uniqueId;
        }
        return "";
    }

    get groupedAttendees() {
        if (this.event.attendeeBased) {
            return this.registrationModel.attendees;
        }

        let attendees: Attendee[] = [];

        this.registrationModel.attendees.forEach(attendee => {
            var foundAttendee = attendees.find(item => item.matches(attendee));
            if (foundAttendee) {
                attendee.selectedTickets.forEach(ticket => {
                    if (foundAttendee) {
                        foundAttendee.selectedTickets.push(cloneDeep(ticket));
                    }
                });
            }
            else {
                attendees.push(cloneDeep(attendee));
            }
        });

        return attendees;
    }

    // Private Methods
    private whoIsGoingStep: Step = {
        name: "who-is-going",
        title: "Who's going?",
        iconClass: "fas fa-user",
        completed: false
    };

    private ticketsStep: Step = {
        name: "tickets",
        title: "Tickets",
        iconClass: "fas fa-cart-plus",
        completed: false
    };

    private checkoutStep: Step = {
        name: "checkout",
        title: "Checkout",
        iconClass: "fas fa-shopping-cart",
        completed: false
    };

    private loadSteps() {
        if (this.event.attendeeBased) {
            this.currentStep = this.whoIsGoingStep;
            this.steps.push(this.whoIsGoingStep);
            this.steps.push(this.ticketsStep);
        }
        else {
            this.currentStep = this.ticketsStep;
            this.steps.push(this.ticketsStep);
            this.steps.push(this.whoIsGoingStep);
        } 

        this.steps.push(this.checkoutStep);
    }

    private moveToNextStep() {
        var moveNextStep = true;
        this.hasErrorOnContinue = false;
        this.errorMessage = '';

        if (this.event.attendeeBased) {
            if (this.currentStep == this.whoIsGoingStep) {
                if (!this.currentUser.isLoggedIn && this.allItemsRestrictedForGuestPurchase) {
                    moveNextStep = false;
                    this.hasErrorOnContinue = true;
                    this.errorMessage = 'Please login to register';
                    return;
                }
                else if (this.isEmpty(this.registrationModel.attendees)) {
                    moveNextStep = false;
                    this.hasErrorOnContinue = true;
                    this.errorMessage = 'You must select at least one attendee before proceeding to the next step';
                    return;
                }
                else if (!this.currentUser.isLoggedIn && !this.isRegisteringAsGuest) {
                    moveNextStep = false;
                    this.hasErrorOnContinue = true;
                    this.errorMessage = 'You must login or register as guest to continue.';
                    return;
                }
            }
            else if (this.currentStep == this.ticketsStep) {
                Object.keys(this.currentAttendee.attendeeCustomFieldValues).forEach(key => {
                    // Check if `customFieldId` is missing on any custom field values
                    if (!this.currentAttendee.attendeeCustomFieldValues[key].hasOwnProperty('customFieldId')) {
                        // Assign `customFieldId` based on the key
                        this.currentAttendee.attendeeCustomFieldValues[key].customFieldId = parseInt(key);
                    }
                });

                // Make sure they've selected a ticket
                if (this.isEmpty(this.currentAttendee.selectedTickets)) {
                    moveNextStep = false;
                    this.hasErrorOnContinue = true;
                    this.errorMessage = 'Please choose a ticket.';
                    return;
                }

                // If this isn't the last attendee, then proceed to the next one
                if (this.currentAttendeeIndex < (this.registrationModel.attendees.length - 1)) {
                    this.currentAttendeeIndex++;
                    moveNextStep = false;
                } else {
                    // reset the index in case users go back
                    this.currentAttendeeIndex = 0;
                }
            }
        }
        else {
            if (this.currentStep == this.ticketsStep) {
                if (!this.currentUser.isLoggedIn && this.allItemsRestrictedForGuestPurchase) {
                    moveNextStep = false;
                    this.hasErrorOnContinue = true;
                    this.errorMessage = 'Please login to register';
                    return;
                }
                // Make sure they've selected a ticket
                else if (this.isEmpty(this.registrationModel.purchasedTickets)) {
                    moveNextStep = false;
                    this.hasErrorOnContinue = true;
                    this.errorMessage = 'Please choose a ticket.';
                    return;
                }
                else if (!this.currentUser.isLoggedIn && !this.isRegisteringAsGuest) {
                    moveNextStep = false;
                    this.hasErrorOnContinue = true;
                    this.errorMessage = 'You must login or register as guest to continue.';
                    return;
                }
            }
            else if (this.currentStep == this.whoIsGoingStep) {
                let errorMessage = "";
                this.groupedAttendees.forEach(attendee => {
                    let duplicates: Ticket[] = [];

                    attendee.selectedTickets.forEach(ticket => {
                        if (attendee.customerId && attendee.customerId > 0) {
                            if (attendee.selectedTickets.filter(item => item.id == ticket.id).length > 1 && !duplicates.find(item => item.id == ticket.id)) {
                                duplicates.push(ticket);
                                errorMessage += ticket.name + " ticket is selected more than once for attendee " + attendee.name + ".\n"; 
                            }
                        }
                    });
                });

                if (errorMessage != "") {
                    moveNextStep = false;
                    this.hasErrorOnContinue = true;
                    this.errorMessage = errorMessage;
                    return;
                }
            }
        }

        if (moveNextStep) {
            this.currentStep.completed = true;
            this.currentStep = this.steps[this.steps.indexOf(this.currentStep) + 1];
        }

        this.scrollToTop();
    }

    private scrollToTop() {
        window.scrollTo({ top: (<HTMLElement>this.$refs.container).offsetTop });
    }

    resetFocus() {
        let containerEl = <HTMLElement>this.$refs.container;
        if (containerEl) {
            containerEl.focus();
        }
    }    

    private loadEvent() {
        this.isLoadingEvent = true;
        return eventRepo.getEvent(noviNs.eventId)
            .then(dtoEvent => {
                this.event = Object.assign(this.event, dtoEvent);
                this.registrationModel.eventId = this.event.id;
            })
            .catch(this.getLoadingErrorHandler("Unable to load the event information."))
            .finally(() => {
                this.isLoadingEvent = false;
                this.loadSteps();
        });
    }

    private loadEligibleAttendees() {
        this.isLoadingEligibleAttendees = true;
        eventRepo.getEligibleAttendees(noviNs.eventId)
            .then(dtoEligibleAttendees => {     
                this.eligibleAttendees = dtoEligibleAttendees; // Don't use Object.assign here - The attendee selector won't recompute its options if you do
            })
            .catch(this.getLoadingErrorHandler("Unable to load the eligible attendees."))
            .finally(() => {
                this.isLoadingEligibleAttendees = false;
                this.selectCurrentUserAsAttendee();
            });
    }

    private loadTicketsAndAddons() {
        var self = this;
        this.isLoadingTicketsAndAddons = true;
        eventRepo.getTicketsAndAddons(noviNs.eventId)
            .then(ticketsAndAddons => {
                Object.assign(self.ticketsAndAddons, ticketsAndAddons);
            })
            .catch(self.getLoadingErrorHandler("Unable to load the tickets and addons."))
            .finally(() => {
                self.isLoadingTicketsAndAddons = false;
                if (this.ticketsAndAddons.allItemsAreFree) {
                    this.checkoutStep.title = "Confirm";
                }
                if (!this.isEmpty(this.ticketsAndAddons.products)) {
                    this.ticketsStep.title = "Tickets & Add-ons";
                }
            });
    }

    private loadCurrentUser() {
        var self = this;
        return customerRepo.getCurrentUser()
            .then(user => {
                Object.assign(this.currentUser, user);
            })
            .catch(self.getLoadingErrorHandler("Unable to load current user."))
            .finally(() => {
            });
    }

    private selectCurrentUserAsAttendee() {
        if (this.event.attendeeBased && !this.event.alreadyAttending && this.currentUser.customerID) {
            var currentUser = this.eligibleAttendees.find(item => item.customerId == this.currentUser.customerID);
            if (currentUser) {
                this.registrationModel.attendees.push(createAttendee(currentUser));
            }
        }
    }

    private getLoadingErrorHandler(message: string) {
        const comp = this;
        return function (axiosResponse: AxiosResponse<ApiErrorResponse>) {
            if (axiosResponse && axiosResponse.data && axiosResponse.data.message) {
                comp.globalErrorMessage = `${message} ${axiosResponse.data.message}`;
            } else {
                comp.globalErrorMessage = `${message}`;
            }

            //comp.hasError = true;
            throw axiosResponse;
        }
    }

    // public methods
    saveAndNext() {
        // clear error message in case validation find a different error.
        this.errorMessage = '';
        this.hasErrorOnContinue = false;

        this.$validator.validate()
            .then((valid: any) => {
                if (valid) {
                    this.moveToNextStep();
                }
                else {
                    ValidationService.scrollToFirstInvalidField(this.$el);
                }
            });
    }

    back() {
        if (this.event.attendeeBased && this.currentStep == this.ticketsStep && this.currentAttendeeIndex > 0) {
            this.currentAttendeeIndex--;            
        }
        else {
            this.currentStep = this.steps[this.steps.indexOf(this.currentStep) - 1];
        }

        this.scrollToTop();
    }

    onAttendeeUpdated(attendee: SelectListItemWithSubtext) {
        if (attendee != null && this.event.attendeeBased) {
            if (attendee.id == 0) {
                this.registrationModel.attendees.push(createAttendeeFromSelectListItemWithSubText(attendee));
            }
            else if (attendee.id == -1) {
                this.registrationModel.attendees.push(createAttendeeFromSelectListItemWithSubText({ id: -1, text: "TBD" }));
            }
            else {
                var selectedAttendee = this.eligibleAttendees.find(item => item.customerId == attendee.id);
                if (selectedAttendee != null) {
                    this.registrationModel.attendees.push(createAttendee(selectedAttendee));
                }
            }
        }

        EventBus.$emit("attendee-selector-change");
    }

    removeAttendee(attendee: Attendee) {
        var index = this.registrationModel.attendees.indexOf(attendee);

        // if we are deleting the last attendee then go back to the who is going step
        if (this.registrationModel.attendees.length == 1) {
            this.currentStep = this.whoIsGoingStep;
        }

        // leave the index alone unless we are deleting the last attendee while having more than 1 attendee
        if (this.registrationModel.attendees.length == (index + 1)) {
            this.currentAttendeeIndex = Math.max(this.currentAttendeeIndex - 1, 0);
        }

        if (index != -1) {
            this.registrationModel.attendees.splice(index, 1);
        }
    }

    get allItemsRestrictedForGuestPurchase() {
        if (!this.ticketsAndAddons) {
            //if tickets are still not loaded
            return false;
        }

        return !this.ticketsAndAddons.ticketCategories
            .some(category => category.tickets.findIndex(ticket => ticket.restrictionTitle == null) != -1)
    }

    get guestNotAllowedMessage() {
        if (this.event.purchaserHasManagementAccess && noviNs.allowUsersToRegisterOtherStaffForEvents) {
            return "Can't find someone? Make sure to add them in your <a href=\"" + noviNs.memberCompassUrl + "#staff\" target=\"_blank\"><strong>Member Compass</strong></a> first.";
        }

        return "Can't find someone? Please <a href=\"" + noviNs.contactUsUrl + "\" target=\"_blank\"><strong>contact us</strong></a>.";
    }

    hideGuestAlert() {
        this.hideGuestLoginAlert = true;
        this.isRegisteringAsGuest = true;
    }

    login() {
        window.location.href = "/login?returnUrl=" + window.location.pathname;
        //window.location.pathname
    }

    backToFirstStep() {
        this.currentStep = this.steps[0];
    }

    showAttendee(attendee: Attendee) {
        this.hasErrorOnContinue = false;
        this.errorMessage = '';
        this.currentAttendeeIndex = this.registrationModel.attendees.findIndex(x => x.matches(attendee));
        this.currentStep = this.event.attendeeBased ? this.ticketsStep : this.whoIsGoingStep;
    }

    showAddon() {
        this.hasErrorOnContinue = false;
        this.errorMessage = '';
        this.currentStep = this.ticketsStep;
    }

    noviStepperEntered() {
        // For ticket based events on the whos going step if we have a currentAttendeeIndex with a value greater than 0 then scroll to
        // the nearests attendee from
        if (!this.event.attendeeBased && this.currentStep == this.whoIsGoingStep && this.currentAttendeeIndex > 0) {
            // get first attendee form associated to the current attendee.
            var form = window.document.getElementById('attendee-form-' + this.currentAttendee.uniqueId);
            
            if (form != null) {
                form.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' })
            }
        }
        // If we get here then reset the currentAttendeeIndex in case we go back to the tickets step after leaving.
        else if (!this.event.attendeeBased || this.currentStep != this.ticketsStep) {
            this.currentAttendeeIndex = 0;
        }

        this.hasErrorOnContinue = false;
        this.errorMessage = '';

        this.resetFocus();
    }

    private isEmpty(array: any[]) {
        return !array || !array.length;
    }

    private updateEmailToValidate(email: string): void {
        this.emailToCheck = email;
    }


    // Debounce the function when necessary
    private debouncedCheckUniqueEmail = debounce(this.checkUniqueEmail.bind(this), 1000);

    private validateUniqueEmail(validator: any): void {
        validator.errors.remove('email');
        this.debouncedCheckUniqueEmail(validator);
    }

    private checkUniqueEmail(validator: any): void {
        if (!this.emailToCheck) {
            return;
        }

        if (this.emailToCheck && this.event.attendeeBased && this.event.requireUniqueEmailPerAttendee) {
            // Check emails inside of current registration first
            const hasDuplicate = this.checkImmediateUniqueEmails(validator);

            if (hasDuplicate) {
                // Add error only if it doesn't already exist
                if (!validator.errors.has('email')) {
                    validator.errors.add({
                        field: 'email',
                        msg: 'This email is already linked to one or more selected tickets for this event.',
                    });
                }
            }
            else {
                this.checkAllUniqueEmails(validator);
            }
        }
    }

    private checkImmediateUniqueEmails(validator: any): boolean {
        // Utility function to normalize emails for consistent comparison
        const normalizeEmail = (email: string | null): string => email?.toLowerCase().trim() || '';

        // Normalize the email to check
        const thisEmail = normalizeEmail(this.emailToCheck);

        // If the email is empty or invalid, return false immediately
        if (!thisEmail.length) {
            return false;
        }

        // Generalized function to check for duplicates
        const hasDuplicateTickets = (attendees: { email: string; ticketId: number }[]): boolean => {
            const seen = new Set<string>();
            for (const { email, ticketId } of attendees) {
                if (!email.length) {
                    // Skip processing for empty or invalid emails
                    continue;
                }

                const key = `${email}-${ticketId}`;
                if (seen.has(key)) {
                    return true; // Duplicate found
                }

                seen.add(key);
            }

            return false; // No duplicates
        };

        // Prepare data for checking duplicates in the registration model
        const newAttendeesWithTickets: { email: string; ticketId: number }[] =
            this.registrationModel.attendees
                ?.filter(attendee => normalizeEmail(attendee.email) === thisEmail) // Filter matching attendees
                .flatMap(attendee =>
                    attendee.selectedTickets.map(ticket => ({
                        email: normalizeEmail(attendee.email),
                        ticketId: ticket.id
                    }))
                ) || [];

        return hasDuplicateTickets(newAttendeesWithTickets);
    }

    private checkAllUniqueEmails(validator: any): void {
        
        const ticketIds = this.registrationModel.attendees.flatMap((attendee: any) => attendee.selectedTickets.map((ticket: any) => ticket.id));

        eventRepo.checkAttendeeDuplicateEmails(this.event.uniqueId, this.emailToCheck, ticketIds)
            .then((data) => {
                if (data.IsDuplicateEmail) {
                    // Add error only if it doesn't already exist
                    if (!validator.errors.has('email')) {
                        validator.errors.add({
                            field: 'email',
                            msg: 'This email is already linked to one or more selected tickets for this event.',
                        });
                    }
                }
            })
            .catch((error) => {
                console.error('Error checking duplicate email:', error);
        });
    }

    private onGuestEmailChanged(email: string) {
        const normalized = (email || '').trim();

        if (!normalized || this.emailToCheck === normalized) {
            return;
        }

        this.emailToCheck = normalized;

        if (this.guestEmail == '') {
            this.guestEmail = normalized;
        }

        //need to validate the email before sending the update to avoid unnecessary calls to the server when user is still typing or has deleted the email
        if (this.abandonmentRemindersEnabled && this.isValidEmail(normalized)) {
            this.updateGuestRegistrationActivity(normalized, this.guestEmail);
        }

        this.guestEmail = normalized;
    }

    private updateGuestRegistrationActivity(email: string, previousEmail: string) {
        eventRepo.updateGuestRegistrationActivity(this.event.id, email, previousEmail);
    }


    private updateCustomerRegistrationActivity() {
        const customerId = Number(this.currentUser.customerID);
        eventRepo.updateCustomerRegistrationActivity(this.event.uniqueId, customerId);
    }

    private get abandonmentRemindersEnabled(): boolean {
        const v: any = noviNs.eventRegistrationAbandonmentReminderEmailEnabled;
        if (typeof v === 'boolean') return v;
        if (typeof v === 'string') return v.toLowerCase() === 'true';
        return false;
    }

    private get isImpersonating(): boolean {
        const v: any = noviNs.isImpersonating;
        if (typeof v === 'boolean') return v;
        if (typeof v === 'string') return v.toLowerCase() === 'true';
        return false;
    }

    private isValidEmail(email: string): boolean {
        // Matches the server-side Helpers.EmailValidationExpression
        const emailRegex = /^[^,;]{1,64}@[^,;]{1,255}\.[^,;]{1,60}[^.]$/;
        return emailRegex.test(email);
    }
}